import {
  DateField,
  DateRangeValidation,
  EmailField,
  EmailValidation,
  EnglishNameValidation,
  FieldValidation,
  FileTypeValidation,
  HebrewNameValidation,
  IdNumberValidation,
  IFormField,
  IValidationHandler,
  LengthValidation,
  MaskValidation,
  MaxLengthValidation,
  MinFileSizeValidation,
  MinLengthValidation,
  MobilePhoneField,
  MobilePhoneValidation,
  NumericField,
  RequiredValidation,
  SelectField,
  TextField
} from '@partners/forms';
import {
  ACCEPTABLE_IMAGE_FILE_MIME_TYPES,
  dateMask,
  DateUtil,
  mergeArrays,
  mobilePhoneMask,
  numericMask
} from '@partners/utils';
import { validatorTypeComparer } from './validation.functions';

export const createHebrewNameField = (args: IFormField<string>): TextField<string> => {
  let validators: IValidationHandler[] = [
    new RequiredValidation(),
    new HebrewNameValidation(),
    new MinLengthValidation({ min: 2, message: 'בטוח שהקלדת נכון? צריך להקליד בדיוק כמו שמופיע בתעודת הזהות' }),
    new MaxLengthValidation({ max: 25, message: 'בטוח שהקלדת נכון? צריך להקליד בדיוק כמו שמופיע בתעודת הזהות' })
  ];

  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length) {
    /** extend the default "hebrewName" validation with custom validation */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators, label: validation?.label }) }
  );

  return new TextField<string>({ ...args });
};

export const createEnglishNameField = (args: IFormField<string>): TextField<string> => {
  let validators: IValidationHandler[] = [
    new RequiredValidation(),
    new EnglishNameValidation({ message: 'שם פרטי צריך להכיל לפחות 2 אותיות באנגלית בלבד, ובלי 3 אותיות זהות ברצף' }),
    new MinLengthValidation({ min: 2 }),
    new MaxLengthValidation({ max: 25 })
  ];

  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length) {
    /** extend the default "hebrewName" validation with custom validation */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign({}, { ...args }, { validation: new FieldValidation({ validationHandlers: validators }) });

  return new TextField<string>({ ...args });
};

export const createEmailField = (args: IFormField<string>): EmailField<string> => {
  let validators: IValidationHandler[] = [
    new RequiredValidation({ message: 'נראה שחסרה כאן כתובת מייל' }),
    new LengthValidation({ min: 7, max: 50 }),
    new EmailValidation({ message: 'נראה שיש טעות במייל, כדאי לבדוק שוב' })
  ];

  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length) {
    /** extend the default "hebrewName" validation with custom validation */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    {
      inputType: 'email',
      bidi: true,
      label: 'כתובת מייל'
    },
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators }) }
  );

  return new TextField<string>({ ...args });
};

export const createMobilePhoneField = (args: IFormField): TextField<string> => {
  let validators: IValidationHandler[] = [
    new RequiredValidation(),
    new MaskValidation(),
    new MobilePhoneValidation({ message: 'בטוח שהקלדת נכון? צריך להקליד 10 ספרות בדיוק' })
  ];
  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length) {
    /** extend the default "hebrewName" validation with custom validation */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    {
      inputType: 'tel',
      pattern: '[0-9]*',
      path: 'phoneNumber',
      inputMode: 'numeric',
      masking: { ...mobilePhoneMask() },
      updateOn: 'blur',
      label: 'מספר טלפון'
    },
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators }) }
  );
  return new MobilePhoneField<string>({ ...args });
};

export const createIdNumberField = <T = number>(args: IFormField): NumericField<T> => {
  let validators: IValidationHandler[] = [
    new RequiredValidation(),
    new LengthValidation({ min: 9, max: 9, message: 'בטוח שהקלדת נכון? צריך להקליד 9 ספרות בדיוק' }),
    new MaskValidation({ message: 'בטוח שהקלדת נכון? צריך להקליד בדיוק כפי שמופיע בתעודת הזהות' }),
    new IdNumberValidation({ message: 'בטוח שהקלדת נכון? צריך להקליד בדיוק כפי שמופיע בתעודת הזהות' })
  ];

  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length) {
    /** extend the default "hebrewName" validation with custom validation */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    {
      masking: { ...numericMask({ numOfDigits: 9 }) },
      // updateOn: 'blur',
      label: 'מספר תעודת זהות',
      bidi: true
    },
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators }) }
  );

  return new NumericField<T>({ ...args });
};

export const createBirthDateField = (args: IFormField<string>): DateField<string> => {
  let validators: IValidationHandler[] = [
    new DateRangeValidation({
      min: DateUtil.dateDiff({ value: -99, unit: 'years' }),
      max: DateUtil.dateDiff({ value: -18, unit: 'years' })
    }),
    new RequiredValidation(),
    new MaskValidation()
  ];

  /** check if an overriding defs were passed in args */
  const { validation } = args;

  if (validation?.validationHandlers.length > 0) {
    /** extend the default "birthDate" validation with custom validation
     *  note that any handlers provided in args will be replaced in validators at the same index! */
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    {
      masking: { ...dateMask() },
      pattern: '[0-9]*',
      path: 'birthDate',
      inputType: 'date',
      inputMode: 'numeric',
      bidi: true,
      updateOn: 'blur',
      label: 'תאריך לידה',
      minorLabel: 'לדוגמא: 10/04/1985'
    },
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators }) }
  );

  return new DateField<string>({ ...args });
};

export const createGenderField = (args: IFormField<number>): SelectField<number> => {
  args = Object.assign(
    {},
    {
      keyProp: 'key',
      valueProp: 'value',
      path: 'gender',
      label: 'מה כתוב לך בתעודת הזהות?',
      validation: new FieldValidation({
        validationHandlers: [new RequiredValidation({ message: 'יש לבחור מין' })]
      })
    },
    { ...args }
  );

  return new SelectField<number>({ ...args });
};
export const createDocToUploadField = (args: IFormField<string>): TextField<string> => {
  let validators = [
    // new MaxFileSizeValidation({ maxFileSize: 5, unit: 'MB' }),
    new MinFileSizeValidation({ minFileSize: 50, unit: 'KB' }),
    new FileTypeValidation({ validFileTypes: ACCEPTABLE_IMAGE_FILE_MIME_TYPES, enableBase64String: true })
  ] as IValidationHandler[];

  const { validation } = args;

  if (validation?.validationHandlers.length > 0) {
    validators = mergeArrays(validators, validation.validationHandlers, validatorTypeComparer);
  }

  args = Object.assign(
    {},
    {
      inputType: 'file',
      label: 'העלאת מסמך',
      minorLabel: 'לדוגמא: תעודת זהות'
    },
    { ...args },
    { validation: new FieldValidation({ validationHandlers: validators }) }
  );

  return new TextField<string>({ ...args });
};
